import { SCORED_OXFORD_QUESTION_IDS } from './proInstrument';
import type { AsesInstrumentConfig, AsesQuestion } from './proInstrument';
import { WpLeadWidget } from '../contexts/lead';
import { WpSlideWidget } from '../contexts/pitch';
import { findLeadWidgetByWidgetId } from './proFollowUp';

export type OxfordAnswers = Record<string, string | number>;

export function isOxfordQuestionAnswered(question: AsesQuestion, answers: OxfordAnswers): boolean {
  const value = answers[question.id];
  if (value === undefined || value === '') {
    return false;
  }
  return !Number.isNaN(Number(value));
}

export function canAdvanceFromOxfordQuestion(question: AsesQuestion, answers: OxfordAnswers): boolean {
  return isOxfordQuestionAnswered(question, answers);
}

export function isScoredOxfordAnswerComplete(answers: OxfordAnswers): boolean {
  for (const id of SCORED_OXFORD_QUESTION_IDS) {
    const value = answers[id];
    if (value === undefined || value === '' || Number.isNaN(Number(value))) {
      return false;
    }
  }
  return true;
}

export function areAllOxfordQuestionsAnswered(
  instrumentConfig: AsesInstrumentConfig,
  values?: string[] | null,
): boolean {
  const answers = parseOxfordAnswers(values);
  let questionCount = 0;

  for (const section of instrumentConfig.sections) {
    for (const question of section.questions) {
      questionCount += 1;
      if (!isOxfordQuestionAnswered(question, answers)) {
        return false;
      }
    }
  }

  return questionCount > 0;
}

export function buildOxfordLeadValues(answers: OxfordAnswers): string[] {
  return [JSON.stringify(answers)];
}

export function buildOxfordApiValues(answers: OxfordAnswers): string[] {
  if (!isScoredOxfordAnswerComplete(answers)) {
    return [JSON.stringify(answers)];
  }
  return SCORED_OXFORD_QUESTION_IDS.map((id) => String(answers[id]));
}

export function parseOxfordAnswers(values?: string[] | null): OxfordAnswers {
  if (!values?.length) {
    return {};
  }

  if (
    values.length === SCORED_OXFORD_QUESTION_IDS.length
    && values.every((value) => /^-?\d+$/.test(value))
  ) {
    const answers: OxfordAnswers = {};
    SCORED_OXFORD_QUESTION_IDS.forEach((id, index) => {
      answers[id] = parseInt(values[index], 10);
    });
    return answers;
  }

  const first = values[0];
  if (first?.startsWith('{')) {
    try {
      return JSON.parse(first) as OxfordAnswers;
    } catch {
      return {};
    }
  }

  if (first?.startsWith('[')) {
    try {
      const parsed = JSON.parse(first) as number[];
      if (parsed.length === SCORED_OXFORD_QUESTION_IDS.length) {
        const answers: OxfordAnswers = {};
        SCORED_OXFORD_QUESTION_IDS.forEach((id, index) => {
          answers[id] = parsed[index];
        });
        return answers;
      }
    } catch {
      return {};
    }
  }

  return {};
}

export function areCampaignOxfordWidgetsComplete(
  widgets: WpSlideWidget[],
  leadWidgets: WpLeadWidget[],
  shouldRender: (widget: WpSlideWidget) => boolean,
): boolean {
  const oxfordWidgets = widgets.filter((widget) => shouldRender(widget));
  if (!oxfordWidgets.length) {
    return true;
  }

  for (const widget of oxfordWidgets) {
    const instrumentConfig = widget.instrumentConfig;
    if (!instrumentConfig?.sections?.length) {
      return false;
    }
    const leadWidget = findLeadWidgetByWidgetId(leadWidgets, widget.id);
    if (!areAllOxfordQuestionsAnswered(instrumentConfig, leadWidget?.values)) {
      return false;
    }
  }

  return true;
}

export function transformOxfordWidgetValuesForSubmit(
  leadWidgets: WpLeadWidget[],
  campaignWidgets: WpSlideWidget[],
  shouldRender: (widget: WpSlideWidget) => boolean,
): WpLeadWidget[] {
  return leadWidgets.map((leadWidget) => {
    const campaignWidget = campaignWidgets.find((widget) => widget.id === leadWidget.widgetId);
    if (!campaignWidget || !shouldRender(campaignWidget)) {
      return leadWidget;
    }
    const answers = parseOxfordAnswers(leadWidget.values);
    return {
      ...leadWidget,
      values: buildOxfordApiValues(answers),
    };
  });
}
